import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import CheckboxSelectItem from 'components/checkbox-select-item';
import FilterSidebar from 'components/filter-sidebar';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Pagination from 'components/pagination';

import Paper from 'components/paper';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import { usePagination } from 'hooks/use-pagination';
import { CheckCircle } from 'lucide-react';
import Mui from 'materials';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { OwnerType } from 'types/shared/owner-type.enum';
import { ProfileType } from 'types/shared/profile-type.enum';
import _ from 'underscore';
import ProfilesStrategiesTabHeader from '../../components/profile-strategies-tabs-header';
import { getProfilesByType } from './api/get-profiles';

type Props = {};

const ListProfiles: React.FC<Props> = () => {
  const intl = useIntl();
  const history = useHistory();
  const { account } = useAccountState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const PAGE_NAME_ID = 'profilesStrategies.listProfiles';
  const PAGINATION_LIMIT = 10;

  // #region STATE

  const [ownerType, setOwnerType] = useState<OwnerType>(OwnerType.MARKET);
  const [profileType, setProfileType] = useState<ProfileType>(ProfileType.EGI);
  const [siteId, setSiteId] = useState(_.first(account.company!.addressAccess.filter((site) => site.seaCloudId))?.id);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['active']);
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);

  // #endregion STATE

  const getTypeId = () => {
    switch (ownerType) {
      case OwnerType.MARKET:
        return account.company?.marketId;
      case OwnerType.COMPANY:
        return account.company?.companyId;
      case OwnerType.SITE:
        return siteId;
    }
  };

  // #region FETCH DATA

  const { data: profiles, isLoading } = useGet(
    ['getProfileListByType', profileType, ownerType, getTypeId()],
    (context) => getProfilesByType(context.queryKey[1] as ProfileType, context.queryKey[2] as OwnerType, String(context.queryKey[3])),
    {
      enabled: !!ownerType && !!profileType,
    }
  );

  // #endregion FETCH DATA

  // #region HANDLERS

  const handleOnClick = (url: string) => {
    history.push(url);
  };

  // #endregion HANDLERS

  // #region RENDERS

  const renderRows = () => {
    const rows = _.chain(profiles)
      .filter((item) => _.contains(selectedFilters, 'active') === item.active)
      .filter((item) => _.contains(selectedFilters, 'isTemporary') === item.isTemporary)
      .sortBy((i) => i.name.toLowerCase())
      .value();
    return rows;
  };

  // #endregion RENDERS

  return (
    <>
      <ProfilesStrategiesTabHeader utils={<Breadcrumbs />} />
      <Paper transparent>
        <FilterSidebar>
          <Box display={'flex'} sx={{ flexDirection: { md: 'row', xs: 'column', sm: 'row' } }} gap={3}>
            <Select
              label={intl.formatMessage({ id: `profilesStrategies.ownerTitle` })}
              margin="none"
              value={ownerType}
              onChange={(event) => setOwnerType(event.target.value as OwnerType)}>
              <RadioSelectItem value={OwnerType.MARKET} selected={OwnerType.MARKET === ownerType}>
                <FormattedMessage id={`profilesStrategies.menuItem.market`} />
              </RadioSelectItem>
              <RadioSelectItem value={OwnerType.COMPANY} selected={OwnerType.COMPANY === ownerType}>
                <FormattedMessage id={`profilesStrategies.menuItem.company`} />
              </RadioSelectItem>
              <RadioSelectItem value={OwnerType.SITE} selected={OwnerType.SITE === ownerType}>
                <FormattedMessage id={`profilesStrategies.menuItem.site`} />
              </RadioSelectItem>
            </Select>
            {ownerType === OwnerType.SITE && (
              <Select
                label={intl.formatMessage({ id: `profilesStrategies.menuItem.site` })}
                margin="none"
                value={siteId}
                onChange={(event) => setSiteId(String(event.target.value))}>
                {_.chain(account.company?.addressAccess)
                  .sortBy((s) => s.name.toLowerCase())
                  .value()
                  .map((site) => (
                    <RadioSelectItem key={site.id} value={site.id} selected={siteId === site.id}>
                      {site.name}
                    </RadioSelectItem>
                  ))}
              </Select>
            )}
            <Select
              label={intl.formatMessage({ id: `profilesStrategies.label.profile` })}
              margin="none"
              value={profileType}
              onChange={(event) => setProfileType(event.target.value as ProfileType)}>
              <RadioSelectItem value={ProfileType.EGI} selected={ProfileType.EGI === profileType}>
                <FormattedMessage id={`profilesStrategies.createProfiles.profiles.egi`} />
              </RadioSelectItem>
              <RadioSelectItem value={ProfileType.EFI} selected={ProfileType.EFI === profileType}>
                <FormattedMessage id={`profilesStrategies.createProfiles.profiles.efi`} />
              </RadioSelectItem>
              <RadioSelectItem value={ProfileType.MORTALITY} selected={ProfileType.MORTALITY === profileType}>
                <FormattedMessage id={`profilesStrategies.createProfiles.profiles.mort`} />
              </RadioSelectItem>
            </Select>
            <Select
              multiple
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.state.title` })}
              value={selectedFilters}
              onChange={(event) => setSelectedFilters(event.target.value as string[])}>
              <CheckboxSelectItem checked={selectedFilters.includes('active')} value={'active'}>
                <FormattedMessage id={`${PAGE_NAME_ID}.filters.state.active`} />
              </CheckboxSelectItem>
              <CheckboxSelectItem checked={selectedFilters.includes('isTemporary')} value={'isTemporary'}>
                <FormattedMessage id={`${PAGE_NAME_ID}.filters.state.isTemporary`} />
              </CheckboxSelectItem>
            </Select>
          </Box>
          <Button onClick={() => handleOnClick(`/profiles-strategies/profiles/create`)}>
            <FormattedMessage id={`${PAGE_NAME_ID}.createButton`} />
          </Button>
        </FilterSidebar>

        <List
          options={[
            { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.nameLabel` }) },
            {
              id: 'numberOfConnectedFishgroups',
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.numberOfConnectedFishGroupsLabel` }),
              align: 'right',
            },
            { id: 'active', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.activeLabel` }), align: 'center' },
            { id: 'isTemporary', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.isTemporaryLabel` }), align: 'center' },
            { id: 'actions', label: '', align: 'right', hide: !isMobile },
          ]}
          orderBy="name"
          isLoading={isLoading}
          noResult={{
            show: renderRows().length <= 0,
            text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
          }}>
          {renderRows()
            .slice(from, to)
            .map((row, idx) => (
              <ListRow key={idx} onClick={() => handleOnClick(`/profiles-strategies/profiles/${profileType}/${row.id}`)}>
                <ListCell align="left">{row.name}</ListCell>
                <ListCell align="right">{row.numberOfConnectedFishgroups}</ListCell>
                <ListCell align={isMobile ? 'right' : 'center'}>
                  <Box>{row.active && <CheckCircle color={theme.palette.darkBlue.main} size={22} />}</Box>
                </ListCell>

                <ListCell align={isMobile ? 'right' : 'center'}>
                  <Box>{row.isTemporary && <CheckCircle color={theme.palette.darkBlue.main} size={22} />} </Box>
                </ListCell>
                <ListCell align="right" sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Button
                    fullWidth={isMobile}
                    size="small"
                    onClick={() => handleOnClick(`/profiles-strategies/profiles/${profileType}/${row.id}`)}>
                    <FormattedMessage id="general.view" />
                  </Button>
                </ListCell>
              </ListRow>
            ))}
        </List>

        <Pagination currentPage={currentPage} pages={Math.ceil(renderRows().length / PAGINATION_LIMIT)} onClick={handlePagination} />
      </Paper>
    </>
  );
};

export default ListProfiles;
